"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { useRouter } from "next/navigation"
import { Calendar, Clock, ArrowRight, Flag } from "lucide-react"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { timelineItems, type TimelineItem, type BlogPost, type MilestoneMarker } from "./blogData"

export default function BlogPage() {
  const [mounted, setMounted] = useState(false)
  const [hoveredPost, setHoveredPost] = useState<number | null>(null)
  const router = useRouter()

  // Glassmorphism style object for reuse
  const glassmorphismStyle = {
    background: 'rgba(255, 255, 255, 0.21)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    borderRadius: '20px',
    border: '0.25px solid rgba(255, 255, 255, 0.1)',
    boxShadow: `
      0 8px 32px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.5),
      inset 0 -1px 0 rgba(255, 255, 255, 0.1),
      inset 0 0 42px 21px rgba(255, 255, 255, 0.05)
    `
  }

  const GlassmorphismHighlights = () => (
    <>
      {/* Top highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)'
        }}
      />
      {/* Left highlight line */}
      <div 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '1px',
          height: '100%',
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.8), transparent, rgba(255, 255, 255, 0.3))'
        }}
      />
    </>
  )

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted) {
    return null
  }

  const handleCardClick = (slug: string) => {
    router.push(`/blog/${slug}`)
  }

  const isBlogPost = (item: TimelineItem): item is BlogPost => {
    return !('type' in item)
  }

  const isMilestone = (item: TimelineItem): item is MilestoneMarker => {
    return 'type' in item && item.type === 'milestone'
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <Badge variant="outline" className="mb-4">The Story of an AI Intern</Badge>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6">
            Intern to 
              <span className="text-primary"> CEO</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              A journey to discover our third co-founder
            </p>
          </motion.div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical timeline line */}
            <div 
              className="absolute h-full w-[2px] bg-gradient-to-b from-primary via-primary/50 to-primary"
              style={{ left: 'calc(50% - 1px)' }}
            ></div>
            
            {/* Timeline items */}
            <div className="space-y-12">
              {timelineItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className={`relative flex items-center min-h-40 ${
                    isBlogPost(item) 
                      ? (index % 2 === 0 ? 'justify-start' : 'justify-end')
                      : 'justify-center'
                  }`}
                  onMouseEnter={() => isBlogPost(item) && setHoveredPost(item.id)}
                  onMouseLeave={() => setHoveredPost(null)}
                >
                  {/* Timeline dot */}
                  <motion.div 
                    className="absolute w-3 h-3 bg-primary rounded-full border-2 border-background z-10"
                    style={{ 
                      left: 'calc(50% - 6px)', // Half of 12px (3 * 4 for w-3)
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                    animate={{
                      scale: (isBlogPost(item) && hoveredPost === item.id) ? 1.5 : 1,
                      boxShadow: (isBlogPost(item) && hoveredPost === item.id) ? '0 0 20px rgba(124, 58, 237, 0.8)' : '0 0 0 rgba(124, 58, 237, 0)'
                    }}
                    transition={{ duration: 0.3 }}
                  />

                  {/* Render Blog Post */}
                  {isBlogPost(item) && (
                    <motion.div 
                      className={`w-5/12 ${index % 2 === 0 ? 'pr-8' : 'pl-8'}`}
                      whileHover={{ scale: 1.02 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card 
                        className="!bg-transparent relative overflow-hidden cursor-pointer transition-all hover:shadow-lg" 
                        style={{
                          ...glassmorphismStyle,
                          transform: hoveredPost === item.id ? 'translateZ(0)' : '',
                        }}
                        onClick={() => handleCardClick(item.slug)}
                      >
                        <GlassmorphismHighlights />
                        <CardContent className="p-4 space-y-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-4 w-4" />
                            <span>{item.date}</span>
                            <Clock className="h-4 w-4 ml-2" />
                            <span>{item.readTime}</span>
                          </div>
                          <h3 className="text-lg font-bold">{item.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {item.excerpt}
                          </p>
                          <motion.div 
                            className="flex items-center gap-1 text-primary font-semibold text-sm"
                            animate={{
                              x: hoveredPost === item.id ? 5 : 0
                            }}
                            transition={{ duration: 0.2 }}
                          >
                            Read more
                            <ArrowRight className="h-4 w-4" />
                          </motion.div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  )}

                  {/* Render Milestone Marker */}
                  {isMilestone(item) && (
                    <div className="w-full relative flex items-center">
                      {/* Milestone Text */}
                      <motion.div 
                        className={`absolute w-5/12 ${
                          index % 2 === 0 ? 'left-0 pl-4' : 'right-0 pr-4'
                        } flex justify-center items-center`}
                        initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 + 0.2 }}
                      >
                        <div className="text-center space-y-2">
                          <h4 className="text-white text-2xl font-bold leading-none">
                            {item.milestone}
                          </h4>
                          {item.subtitle && (
                            <p className="text-gray-400 text-sm leading-relaxed max-w-xs mx-auto">
                              {item.subtitle}
                            </p>
                          )}
                        </div>
                      </motion.div>

                      {/* Flag Icon */}
                      <motion.div 
                        className={`absolute w-5/12 ${
                          index % 2 === 0 ? 'right-0 pr-4' : 'left-0 pl-4'
                        } flex justify-center items-center`}
                        initial={{ opacity: 0, x: index % 2 === 0 ? 20 : -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 + 0.3 }}
                      >
                        <Flag className="w-12 h-12 text-primary" />
                      </motion.div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Destination Section */}
      <section className="py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: timelineItems.length * 0.1 }}
          >
            {/* CEO text and subtitle container */}
            <div className="flex flex-col items-center space-y-4 text-center">
              {/* CEO text */}
              <motion.h2 
                className="text-4xl md:text-5xl font-bold text-white"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: timelineItems.length * 0.1 + 0.2 }}
              >
                CEO
              </motion.h2>
              
              {/* CEO subtitle */}
              <motion.p 
                className="text-gray-400 text-sm leading-relaxed max-w-md"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: timelineItems.length * 0.1 + 0.4 }}
              >
                The AI Intern is capable of handling tasks across multiple functions with efficiency and accuracy and can also provide strategic guidance and recommendations, thus becoming the CEO.
              </motion.p>
            </div>
          </motion.div>
        </div>
      </section>


    </div>
  )
}